import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { NgIdleModule } from '@ng-idle/core';

import { ApiInterceptor } from '../core/interceptor/api.interceptor';
import { LoaderInterceptor } from '../core/interceptor/loader.interceptor';
import { AppInjector } from '../core/common/app.injector';
import { CoreModule } from '../core/core.module';
import { AuthGuardService } from '../core/guard/auth.guard';
import { AuthenticationService } from '../modules/authentication/services/authentication.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PrimeNgModules } from './primeng.module';
import { HttpErrorInterceptor } from '../core/interceptor/error.interceptor';
import localeIn from '@angular/common/locales/en-IN';
import { NgxSpinnerModule } from 'ngx-spinner';
import { CacheClearService } from '../core/services/cache-clear.service';
import { EncodeInterceptor } from 'app/core/interceptor/encode.interceptor';
import { AppInitializerService } from '../core/services/app-initializer.service';

import { environment } from '../../environments/environment';
import {
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

const msalConfig = environment.mslConfig;

// Define your protected resources configuration
export const protectedResources = {
  // Your DRF Backend API
  drfBackendApi: {
    endpoint: environment.drfBackendBaseUrl, // Use from environment
    scopes: [`api://${environment.mslConfig.clientId}/access_as_user`], // Use from environment
  },
  // Microsoft Graph API
  microsoftGraph: {
    endpoint: 'https://graph.microsoft.com/v1.0/', // This is the base URL for Graph API calls
    scopes: ['User.Read', 'Organization.Read.All'], // These are the permissions (scopes) for Graph
  },
};
@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    CoreModule,
    FormsModule,
    NgxSpinnerModule,
    ToastrModule.forRoot(),
    ...PrimeNgModules,
    NgIdleModule.forRoot(),
    MsalModule.forRoot(
      new PublicClientApplication({
        auth: {
          clientId: msalConfig.clientId,
          redirectUri: msalConfig.redirectUri,
          postLogoutRedirectUri: msalConfig.postLogoutRedirectUri,
          authority: msalConfig.authority,                    // 👈 important
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: isIE,
        },
      }),
      {
        interactionType: InteractionType.Redirect, // Msal Guard Configuration
        authRequest: {
          scopes: [
            'openid', // Standard OpenID Connect scope
            'profile', // Standard profile information
            protectedResources.microsoftGraph.scopes[0], // User.Read
            protectedResources.microsoftGraph.scopes[1], // Organization.Read.All
          ],
        },
      },
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        protectedResourceMap: new Map([
          [
            protectedResources.drfBackendApi.endpoint,
            protectedResources.drfBackendApi.scopes,
          ],
          [
            protectedResources.microsoftGraph.endpoint,
            protectedResources.microsoftGraph.scopes, // Map the base Graph URL to ALL Graph scopes it might need
          ],
        ]),
      }
    ),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (appInitializer: AppInitializerService) => () => appInitializer.initializeApp(),
      deps: [AppInitializerService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EncodeInterceptor,
      multi: true,
    },
    AuthGuardService,
    // RoleAuthGuard,
    AuthenticationService,
    CacheClearService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalGuard,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {
  constructor(injector: Injector) {
    AppInjector.setInjector(injector);
    registerLocaleData(localeIn);
  }
}
